'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Search } from 'lucide-react';

import { cn } from '@kit/ui/cn';
import { Input } from '@kit/ui/input';

/**
 * Generic, domain-agnostic settings shell (BAL-493).
 *
 * A purely presentational two-column settings layout: a searchable, grouped
 * left rail of navigation links + the single active surface on the right.
 * Extracted from the auditions/competition `RecordSettingsShell`/`SettingsSidebar`
 * so the artist profile editor can reuse the exact same layout.
 *
 * All domain logic (which sections exist, their labels, active-state, i18n)
 * stays in thin per-domain wrappers that pre-resolve a `SettingsNavGroup[]`
 * with already-translated labels and computed `active` flags. This component
 * owns only the search-filter state and the layout/markup.
 */

export interface SettingsNavItem {
  /** Stable key for React list identity. */
  key: string;
  /** Already-translated, user-facing label (search filters on this). */
  label: string;
  /** Destination href (query-param section link or sibling route). */
  href: string;
  /** Whether this item is the currently-active surface. */
  active: boolean;
}

export interface SettingsNavGroup {
  id: string;
  /** Already-translated group label. */
  label: string;
  items: SettingsNavItem[];
}

const itemClass = (active: boolean) =>
  cn(
    'block w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors',
    active
      ? 'bg-muted text-foreground font-medium'
      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
  );

/**
 * The searchable, grouped left rail. Owns its own search-filter state and
 * substring-filters items by `label`, dropping any group left with no items.
 */
export function SettingsNav({
  groups,
  searchPlaceholder,
  emptyLabel,
}: {
  groups: SettingsNavGroup[];
  searchPlaceholder: string;
  emptyLabel: string;
}) {
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !query || item.label.toLowerCase().includes(query),
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <aside className="lg:w-60 lg:flex-shrink-0">
      <div className="space-y-4 lg:sticky lg:top-6">
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9"
          />
        </div>

        <nav className="space-y-5">
          {visibleGroups.map((group) => (
            <div key={group.id} className="space-y-1">
              <p className="text-muted-foreground px-2 font-mono text-[10px] tracking-[0.14em] uppercase">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.key}>
                    <Link
                      href={item.href}
                      aria-current={item.active ? 'page' : undefined}
                      className={itemClass(item.active)}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {visibleGroups.length === 0 && (
            <p className="text-muted-foreground px-2 text-sm">{emptyLabel}</p>
          )}
        </nav>
      </div>
    </aside>
  );
}

/**
 * Two-column settings shell: the persistent {@link SettingsNav} on the left,
 * the active surface (`children`) on the right.
 */
export function SettingsShell({
  groups,
  searchPlaceholder,
  emptyLabel,
  children,
}: {
  groups: SettingsNavGroup[];
  searchPlaceholder: string;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-8 lg:flex-row">
      <SettingsNav
        groups={groups}
        searchPlaceholder={searchPlaceholder}
        emptyLabel={emptyLabel}
      />
      <div className="min-w-0 flex-1 lg:max-w-4xl">{children}</div>
    </div>
  );
}
